/**
 * Cards Admin API — Module Kat Bousanm × HyperPay
 * Endpoints admin pou jere aplikasyon, kat aktif, rechaj ak logs
 * Base: https://api.bousanm.com/api/cards/admin/
 */
import apiClient from './client';

// ============================================================
// TYPES — Aplikasyon
// ============================================================

export type ApplicationStatus =
    | 'draft'
    | 'submitted'
    | 'pending_review'
    | 'approved'
    | 'rejected'
    | 'provider_rejected'
    | 'sent_to_provider'
    | 'provider_pending'
    | 'awaiting_activation'
    | 'activating'
    | 'card_issued';

export type CardTier = 'standard' | 'premium';

export interface KycInfo {
    kycDocNo?: string;
    kycDocType?: string;
    kycBirthday?: string;
    kycGender?: number;
    kycNationality?: string;
    kycCountry?: string;
    kycDocExpireDate?: string;
    frontDocUrl?: string;
    backDocUrl?: string;
    portraitUrl?: string;
    signatureUrl?: string;
    emergencyContact?: string;
    contractUrl?: string;
}

export interface ApplicantInfo {
    clientId: string;
    fullName: string;
    email?: string;
    phone?: string;
    kycStatus: string;
    kyc?: KycInfo;
}

export interface CardApplication {
    id: string;
    clientId: string;
    status: ApplicationStatus;
    cardTierSlug: CardTier;
    amountPaidOriginal?: number;
    paymentCurrency?: string;
    amountPaidUsdt?: number;
    firstRechargeUsdt?: number;
    fxRateApplied?: number;
    addressLine1?: string;
    addressLine2?: string;
    city?: string;
    country?: string;
    postalCode?: string;
    rejectionReason?: string;
    reviewNote?: string;
    reviewedBy?: string;
    reviewedAt?: string;
    submittedAt?: string;
    createdAt: string;
    updatedAt: string;
    applicant?: ApplicantInfo;
    transactions?: ApplicationTransaction[];
    bousanmFeeUsdt?: number;
    hyperpayFeeUsdt?: number;
    providerApplicationId?: string | null;
    providerCardId?: string | null;
    trackingEvents?: Array<{ status: string; date: string; note?: string }>;
    providerError?: string | null;
}

export interface ApplicationTransaction {
    id: string;
    txType: string;
    amountOriginal: number;
    currency: string;
    amountUsdt?: number;
    confirmedBy?: string | null;
    status: string;
    description?: string;
    createdAt: string;
}

export interface ApplicationsListResponse {
    results: CardApplication[];
    total: number;
    page: number;
    limit: number;
    pendingCount?: number;
    approvedCount?: number;
    rejectedCount?: number;
}

// ============================================================
// TYPES — Kat Aktif
// ============================================================

export type CardStatus = 'ACTIVE' | 'FROZEN' | 'BLOCKED' | 'CANCELLED' | 'INACTIVE' | 'EXPIRED';

export interface ActiveCard {
    id: string;
    clientId: string;
    applicationId?: string;
    hyperPayCardId?: string;
    lastFour?: string;
    productName?: string;
    tier?: CardTier;
    status: CardStatus;
    currency?: string;
    expiryDate?: string;
    createdAt: string;
    updatedAt: string;
    holder?: {
        fullName: string;
        email?: string;
        phone?: string;
    };
    balance?: {
        availableBalance: number;
        currentBalance: number;
        currency: string;
        updatedAt?: string;
    };
}

export interface CardsListResponse {
    results: ActiveCard[];
    total: number;
    page: number;
    limit: number;
}

export interface CardActionResult {
    success: boolean;
    message?: string;
    card?: ActiveCard;
}

// ============================================================
// TYPES — Rechaj
// ============================================================

export type RechargeStatus =
    | 'INITIATED'
    | 'FUNDS_HELD'
    | 'FX_PRICED'
    | 'PROVIDER_RECHARGE_SUBMITTED'
    | 'PROVIDER_CALLBACK_RECEIVED'
    | 'PROVIDER_CONFIRMED_SUCCESS'
    | 'PROVIDER_CONFIRMED_FAIL'
    | 'PROVIDER_REFUND_PENDING'
    | 'PROVIDER_REFUNDED'
    | 'LEDGER_FINALIZED';

export interface Recharge {
    id: string;
    cardId: string;
    clientId: string;
    status: RechargeStatus;
    amountHtg?: number;
    amountUsdt?: number;
    fxRate?: number;
    providerRef?: string;
    failureReason?: string;
    initiatedAt: string;
    completedAt?: string;
    createdAt: string;
    holder?: {
        fullName: string;
        lastFour?: string;
    };
}

export interface RechargesListResponse {
    results: Recharge[];
    total: number;
    page: number;
    limit: number;
}

// ============================================================
// TYPES — Logs
// ============================================================

export type LogLevel = 'info' | 'warn' | 'error' | 'debug';
export type LogAction =
    | 'APPLICATION_SUBMITTED'
    | 'APPLICATION_APPROVED'
    | 'APPLICATION_REJECTED'
    | 'APPLICATION_INFO_REQUESTED'
    | 'CARD_ISSUED'
    | 'CARD_FROZEN'
    | 'CARD_UNFROZEN'
    | 'CARD_BLOCKED'
    | 'CARD_UNBLOCKED'
    | 'CARD_CANCELLED'
    | 'CARD_ACTIVATED'
    | 'RECHARGE_INITIATED'
    | 'RECHARGE_SUCCESS'
    | 'RECHARGE_FAILED'
    | 'RECHARGE_REFUNDED'
    | 'WEBHOOK_RECEIVED'
    | 'THREE_DS_CHALLENGE'
    | 'HYPERPAY_API_CALL'
    | 'KYC_PREFILL'
    | 'ADMIN_ACTION';

export interface CardLog {
    id: string;
    level: LogLevel;
    action: LogAction;
    entityType?: string;
    entityId?: string;
    clientId?: string;
    adminId?: string;
    adminName?: string;
    message: string;
    meta?: Record<string, unknown>;
    ipAddress?: string;
    createdAt: string;
}

export interface LogsListResponse {
    results: CardLog[];
    total: number;
    page: number;
    limit: number;
}

// ============================================================
// TYPES — Stats Dashboard
// ============================================================

export interface CardModuleStats {
    applications: {
        total: number;
        pending: number;
        approved: number;
        rejected: number;
        issued: number;
        todaySubmitted: number;
    };
    cards: {
        total: number;
        active: number;
        frozen: number;
        blocked: number;
    };
    recharges: {
        total: number;
        todayCount: number;
        todayVolumeHtg: number;
        monthVolumeHtg: number;
        failed: number;
    };
}

// ============================================================
// API FUNCTIONS
// ============================================================

export const cardAdminApi = {

    // ── Dashboard stats ──────────────────────────────────────
    async getStats(): Promise<CardModuleStats> {
        const { data } = await apiClient.get('/api/cards/admin/stats');
        return data;
    },

    // ── Applications ─────────────────────────────────────────
    async listApplications(params?: {
        page?: number;
        limit?: number;
        status?: ApplicationStatus | 'all';
        search?: string;
        tier?: CardTier;
        dateFrom?: string;
        dateTo?: string;
    }): Promise<ApplicationsListResponse> {
        const { data } = await apiClient.get('/api/cards/admin/applications', { params });
        return data;
    },

    async getApplication(id: string): Promise<CardApplication> {
        const { data } = await apiClient.get(`/api/cards/admin/applications/${id}`);
        return data;
    },

    async approveApplication(id: string, note?: string): Promise<{ success: boolean; message: string }> {
        const { data } = await apiClient.post(`/api/cards/admin/applications/${id}/approve`, { note });
        return data;
    },

    async rejectApplication(id: string, reason: string): Promise<{ success: boolean; message: string }> {
        const { data } = await apiClient.post(`/api/cards/admin/applications/${id}/reject`, { reason });
        return data;
    },

    async requestMoreInfo(id: string, note: string): Promise<{ success: boolean; message: string }> {
        const { data } = await apiClient.post(`/api/cards/admin/applications/${id}/request-info`, { note });
        return data;
    },

    async retryProviderApplication(id: string): Promise<{ message: string; error?: string }> {
        const { data } = await apiClient.post(`/api/cards/admin/applications/${id}/retry-provider`);
        return data;
    },

    // ── Document URLs (R2 signed URLs) ────────────────────────
    async getDocumentUrl(r2Key: string): Promise<{ url: string; expiresAt: string }> {
        const { data } = await apiClient.get('/api/cards/admin/documents/url', {
            params: { key: r2Key },
        });
        return data;
    },

    // ── Active Cards ──────────────────────────────────────────
    async listCards(params?: {
        page?: number;
        limit?: number;
        status?: CardStatus | 'all';
        search?: string;
        tier?: CardTier;
    }): Promise<CardsListResponse> {
        const { data } = await apiClient.get('/api/cards/admin/cards', { params });
        return data;
    },

    async getCard(id: string): Promise<ActiveCard> {
        const { data } = await apiClient.get(`/api/cards/admin/cards/${id}`);
        return data;
    },

    async freezeCard(id: string, reason?: string): Promise<CardActionResult> {
        const { data } = await apiClient.post(`/api/cards/admin/cards/${id}/freeze`, { reason });
        return data;
    },

    async unfreezeCard(id: string): Promise<CardActionResult> {
        const { data } = await apiClient.post(`/api/cards/admin/cards/${id}/unfreeze`);
        return data;
    },

    async blockCard(id: string, reason: string): Promise<CardActionResult> {
        const { data } = await apiClient.post(`/api/cards/admin/cards/${id}/block`, { reason });
        return data;
    },

    async unblockCard(id: string, note?: string): Promise<CardActionResult> {
        const { data } = await apiClient.post(`/api/cards/admin/cards/${id}/unblock`, { note });
        return data;
    },

    async cancelCard(id: string, reason: string): Promise<CardActionResult> {
        const { data } = await apiClient.post(`/api/cards/admin/cards/${id}/cancel`, { reason });
        return data;
    },

    // ── Recharges ─────────────────────────────────────────────
    async listRecharges(params?: {
        page?: number;
        limit?: number;
        status?: RechargeStatus | 'all';
        cardId?: string;
        clientId?: string;
        dateFrom?: string;
        dateTo?: string;
    }): Promise<RechargesListResponse> {
        const { data } = await apiClient.get('/api/cards/admin/recharges', { params });
        return data;
    },

    async getRecharge(id: string): Promise<Recharge> {
        const { data } = await apiClient.get(`/api/cards/admin/recharges/${id}`);
        return data;
    },

    async retryRecharge(id: string): Promise<{ success: boolean; message: string }> {
        const { data } = await apiClient.post(`/api/cards/admin/recharges/${id}/retry`);
        return data;
    },

    async refundRecharge(id: string, reason: string): Promise<{ success: boolean; message: string }> {
        const { data } = await apiClient.post(`/api/cards/admin/recharges/${id}/refund`, { reason });
        return data;
    },

    // ── Logs ──────────────────────────────────────────────────
    async listLogs(params?: {
        page?: number;
        limit?: number;
        level?: LogLevel | 'all';
        action?: LogAction;
        entityId?: string;
        clientId?: string;
        dateFrom?: string;
        dateTo?: string;
        search?: string;
    }): Promise<LogsListResponse> {
        const { data } = await apiClient.get('/api/cards/admin/logs', { params });
        return data;
    },

    async exportLogs(params?: {
        dateFrom?: string;
        dateTo?: string;
        action?: LogAction;
        format?: 'csv' | 'json';
    }): Promise<Blob> {
        const { data } = await apiClient.get('/api/cards/admin/logs/export', {
            params,
            responseType: 'blob',
        });
        return data;
    },

    async getApplicationTransactions(id: string): Promise<ApplicationTransaction[]> {
        const { data } = await apiClient.get(`/api/cards/admin/applications/${id}/transactions`);
        return Array.isArray(data) ? data : [];
    },

    // ── Config ────────────────────────────────────────────────────
    async getSystemConfigs(): Promise<CardSystemConfig[]> {
        const { data } = await apiClient.get('/api/cards/admin/config');
        return Array.isArray(data) ? data : [];
    },

    async updateSystemConfig(key: string, value: string): Promise<CardSystemConfig[]> {
        const { data } = await apiClient.put(`/api/cards/admin/config/${key}`, { value });
        return Array.isArray(data) ? data : [];
    },

    async getFeeConfigs(): Promise<CardFeeConfig[]> {
        const { data } = await apiClient.get('/api/cards/admin/config/fee-configs');
        return Array.isArray(data) ? data : [];
    },

    async updateFeeConfig(id: string, dto: Partial<CardFeeConfig>): Promise<CardFeeConfig> {
        const { data } = await apiClient.put(`/api/cards/admin/config/fee-configs/${id}`, dto);
        return data;
    },

    async getCardTiers(): Promise<CardTierConfig[]> {
        const { data } = await apiClient.get('/api/cards/admin/config/tiers');
        return Array.isArray(data) ? data : [];
    },

    async updateCardTier(id: string, dto: Partial<CardTierConfig>): Promise<CardTierConfig> {
        const { data } = await apiClient.put(`/api/cards/admin/config/tiers/${id}`, dto);
        return data;
    },

    async getCardAccount(): Promise<CardAccount> {
        const { data } = await apiClient.get('/api/cards/admin/config/card-account');
        return data;
    },

    // ── Card Transactions (HyperPay synced) ───────────────────
    async listCardTransactions(params?: {
        page?: number;
        limit?: number;
        type?: CardTransactionType | 'all';
        status?: CardTransactionStatusType | 'all';
        cardId?: string;
        search?: string;
        dateFrom?: string;
        dateTo?: string;
    }): Promise<CardTransactionsListResponse> {
        const { data } = await apiClient.get('/api/cards/admin/card-transactions', { params });
        return data;
    },

    async getCardTransactionStats(): Promise<CardTransactionStats> {
        const { data } = await apiClient.get('/api/cards/admin/card-transactions/stats');
        return data;
    },
};

// ============================================================
// TYPES — Card Transactions (HyperPay synced)
// ============================================================

export type CardTransactionType = 'purchase' | 'atm' | 'refund' | 'fee' | 'reversal' | 'recharge';
export type CardTransactionStatusType = 'pending' | 'completed' | 'declined' | 'reversed';

export interface CardTransactionItem {
    id: string;
    cardId: string;
    providerTxId: string;
    type: CardTransactionType;
    businessType?: number;
    amount: number;
    currency: string;
    merchantName?: string;
    merchantCategory?: string;
    status: CardTransactionStatusType;
    description?: string;
    transactedAt?: string;
    createdAt: string;
    holder?: {
        fullName: string;
        lastFour?: string;
    };
}

export interface CardTransactionsListResponse {
    results: CardTransactionItem[];
    total: number;
    page: number;
    limit: number;
}

export interface CardTransactionStats {
    overview: {
        totalTransactions: number;
        totalPurchase: number;
        totalATM: number;
        totalFee: number;
        totalRecharge: number;
        totalRefund: number;
        totalSpentUSD: number;
        totalFeesUSD: number;
    };
    evolution: Array<{
        month: string;
        purchase: number;
        atm: number;
        fee: number;
        recharge: number;
        refund: number;
        totalAmount: number;
    }>;
    byType: Array<{
        type: string;
        count: number;
        totalAmount: number;
    }>;
    byStatus: Array<{
        status: string;
        count: number;
    }>;
}

// ============================================================
// TYPES — Config
// ============================================================

export interface CardSystemConfig {
    id: string;
    key: string;
    value: string;
    valueType: 'boolean' | 'string' | 'number' | 'enum';
    description: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface CardFeeConfig {
    id: string;
    cardTier: 'standard' | 'premium';
    applyFeeUsdt: number;
    hyperpayApplyFeeUsdt: number;
    bousanmApplyFeeUsdt: number;
    monthlyFeeUsdt: number;
    rechargeFeePercent: number;
    hyperPayFeePercent: number;
    bousanmFeePercent: number;
    rechargeFeeFixedUsdt: number;
    txFeePercent: number;
    txFeeFixedUsdt: number;
    minRechargeUsdt: number;
    maxRechargeUsdt: number;
    dailySpendLimitUsdt: number;
    monthlySpendLimitUsdt: number;
    referralCommissionPercent: number;
    isActive: boolean;
    updatedAt: string;
}

export interface CardTierConfig {
    id: string;
    name: string;
    slug: 'standard' | 'premium';
    description: string | null;
    hyperpayCardTypeId: string;
    badgeColor: string;
    features: string[] | null;
    maxCardsPerClient: number;
    sortOrder: number;
    isActive: boolean;
    feeConfigId: string;
    updatedAt: string;
}

export interface CardAccount {
    id: string;
    name: string;
    balanceUsd: number;
    balanceUsdt: number;
    totalCollectedFeesUsdt: number;
    lastReconciledAt: string | null;
    updatedAt: string;
}

export default cardAdminApi;
